import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { from, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IdbStorageService {
  constructor(private ionStorage: Storage) {
    this.init();
  }

  private async init() {
    await this.ionStorage.create();
    return;
  }

  // Asynchronous promise methods.

  /**
   * Set an item to the indexedDB storage.
   * @param key - Key of the item.
   * @param value - Value of the item.
   */
  public async setItem(key: string, value: any) {
    return this.ionStorage.set(key, value);
  }

  /**
   * Get an item by key asynchronously.
   * @param key - Key of the item.
   */
  public async getItem(key: string): Promise<any> {
    return this.ionStorage.get(key);
  }

  /**
   * Remove an item by key.
   * @param key - Key of the item.
   */
  public async removeItem(key: string): Promise<any> {
    return this.ionStorage.remove(key);
  }

  /**
   * Clear all keys on the storage.
   * Careful to use the method, as it will clear all data on the storage.
   */
  public clear(): Promise<void> {
    return this.ionStorage.clear();
  }

  // Synchronous methods, with Observables.

  /**
   * Set an item to the indexedDB storage.
   * @param key - Key of the item.
   * @param value - Value of the item.
   */
  public setItemSync(key: string, value: any) {
    return from(this.ionStorage.set(key, value)).pipe(take(1));
  }

  /**
   * Get an item by key synchronously (will return an observable).
   * @param key - Key of the item.
   */
  public getItemSync(key: string): Observable<any> {
    return from(this.ionStorage.get(key)).pipe(take(1));
  }

  /**
   * Remove an item by key.
   * @param key - Key of the item.
   */
  public removeItemSync(key: string) {
    return from(this.ionStorage.remove(key)).pipe(take(1));
  }

  /**
   * Clear all keys on the storage synchronously, will return as an observable.
   * Careful to use the method, as it will clear all data on the storage.
   */
  public clearSync() {
    return from(this.ionStorage.clear()).pipe(take(1));
  }
}
